import DetailSection from '@components/Common/DetailSection';
import FormField from '@components/Common/FormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import { Box, Button, Typography } from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';

/**
 * Add a careers link (owner, 2026-10-06). The service registers it for review and the Scout works
 * out how to read the site, then fetches a first batch. Nothing reaches candidates until a Super
 * Admin approves the source, so this is safe to offer to HR as well.
 */
export interface AddSourceFormProps {
  onAdded: (sourceId: string) => void;
  onCancel: () => void;
}

/** "Tanishq, Mia" to ["Tanishq", "Mia"]. */
export const parseBrands = (v: string): string[] =>
  v
    .split(',')
    .map((b) => b.trim())
    .filter(Boolean)
    .slice(0, 10);

export const isCareersUrl = (v: string): boolean => /^https?:\/\/[^\s/]+\.[^\s]+$/i.test(v.trim());

const AddSourceForm: React.FC<AddSourceFormProps> = ({ onAdded, onCancel }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState({ companyName: '', careersUrl: '', brandNames: '', notes: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const set = (name: string, value: unknown) => setForm((f) => ({ ...f, [name]: String(value ?? '') }));

  const submit = async () => {
    const next: Record<string, string> = {};
    if (!form.companyName.trim()) next.companyName = 'Enter the employer name.';
    if (!isCareersUrl(form.careersUrl)) next.careersUrl = 'Enter the full address, starting with https://';
    setErrors(next);
    if (Object.keys(next).length) {
      document.getElementById(Object.keys(next)[0])?.focus();
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post('/api/external-jobs/sources', {
        companyName: form.companyName.trim(),
        careersUrl: form.careersUrl.trim(),
        brandNames: parseBrands(form.brandNames),
        notes: form.notes.trim() || undefined,
      });
      showToast(`Added ${r.data?.companyName ?? form.companyName}. The Scout is reading the site.`, 'success');
      onAdded(r.data?.sourceId);
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'The link was not added.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailSection title="Add a careers link" icon={<AddLinkOutlinedIcon />} sx={{ mb: 0 }}>
      <Box className="ej-add">
        <Typography variant="body2" color="text.secondary">
          The Scout works out how to read the page and fetches the jobs on it for you to check. Nothing is shown on the
          site until a Super Admin approves the source.
        </Typography>
        <Box className="ej-add-fields">
          <FormField
            required
            name="companyName"
            label="Employer"
            placeholder="Senco Gold"
            value={form.companyName}
            onChange={set}
            errors={errors}
          />
          <FormField
            required
            name="careersUrl"
            label="Careers page"
            type="url"
            placeholder="https://careers.example.com/jobs"
            value={form.careersUrl}
            onChange={set}
            errors={errors}
          />
          <FormField
            required={false}
            name="brandNames"
            label="Brands (optional)"
            placeholder="Separate with commas"
            value={form.brandNames}
            onChange={set}
          />
          <FormField
            required={false}
            name="notes"
            label="Notes (optional)"
            placeholder="Who asked for it, anything the Scout should know"
            value={form.notes}
            onChange={set}
          />
        </Box>
        <Box className="ej-actions">
          <Button variant="contained" onClick={submit} disabled={busy}>
            Add and scout
          </Button>
          <Button variant="outlined" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        </Box>
      </Box>
    </DetailSection>
  );
};

export default AddSourceForm;
